import type { ProductsQueryParams } from '../hooks/useProductsQuery';
import { createdFromParam, createdToParam } from './createdRange';

/**
 * 15-B-3 (ID90) — 상품 엑셀 export 쿼리스트링 빌더. BE `GET /admin/v1/products/export` 는
 * 목록(`GET /admin/v1/products`)과 동일한 검색 필터를 받아 매칭 상품을 .xlsx 로 내보낸다
 * ("화면에서 본 결과"와 1:1). 정렬/페이지는 export 가 자체적으로 createdAt DESC·MAX_ROWS 까지
 * 처리하므로 전송하지 않는다(필터만).
 */
export const buildProductExportQuery = (params: ProductsQueryParams): string => {
  const qs = new URLSearchParams();
  const keyword = params.keyword?.trim();
  if (keyword) qs.set('keyword', keyword);
  if (params.saleStatus) qs.set('saleStatus', params.saleStatus);
  if (params.productType) qs.set('productType', params.productType);
  if (params.categoryId) qs.set('categoryId', params.categoryId);
  if (params.priceMin != null) qs.set('priceMin', String(params.priceMin));
  if (params.priceMax != null) qs.set('priceMax', String(params.priceMax));
  if (params.stockMin != null) qs.set('stockMin', String(params.stockMin));
  if (params.stockMax != null) qs.set('stockMax', String(params.stockMax));
  // 등록일 — 목록과 같은 변환(BE export 도 Instant). 날짜만 보내면 400.
  const createdFrom = createdFromParam(params.createdFrom);
  const createdTo = createdToParam(params.createdTo);
  if (createdFrom) qs.set('createdFrom', createdFrom);
  if (createdTo) qs.set('createdTo', createdTo);
  if (params.artistId) qs.set('artistId', params.artistId);
  if (params.hasArtist != null) qs.set('hasArtist', String(params.hasArtist));
  if (params.displayed != null) qs.set('displayed', String(params.displayed));
  // QA-199 — 누락 필터 보강(상품코드/바코드/옵션보유). 이게 빠져 검색해도 전체가 받아지던 문제 수정.
  const productCode = params.productCode?.trim();
  if (productCode) qs.set('productCode', productCode);
  const barcode = params.barcode?.trim();
  if (barcode) qs.set('barcode', barcode);
  const selfProductCode = params.selfProductCode?.trim();
  if (selfProductCode) qs.set('selfProductCode', selfProductCode);
  if (params.hasOption != null) qs.set('hasOption', String(params.hasOption));
  return qs.toString();
};

/**
 * QA-199 — 전체 필드 export 의 "필드 그룹" 선택. 무거운 상세HTML/다국어 열을 빼면 생성·전송이 크게 빨라진다.
 * 키는 be-catalog ProductFullExcelService.FIELD_KEY_TO_COL 과 1:1(컬럼 키 CSV 로 전송).
 * 기본정보(basic)는 식별·필수라 항상 포함(상품코드 등). 전부 선택 시 fields 파라미터를 생략해 라운드트립 양식(26열) 그대로 받는다.
 */
export interface ProductExportFieldGroup {
  key: string;
  label: string;
  cols: readonly string[];
  always?: boolean;
}

export const PRODUCT_EXPORT_FIELD_GROUPS: readonly ProductExportFieldGroup[] = [
  { key: 'basic', label: '기본정보(코드·유형·상태·진열·카테고리·브랜드)', cols: ['productCode', 'barcode', 'productType', 'status', 'displayed', 'categoryId', 'brandName'], always: true },
  { key: 'price', label: '가격(정가·판매가 4통화)', cols: ['listPrice', 'saleKrw', 'saleUsd', 'saleJpy', 'saleCny'] },
  { key: 'name', label: '다국어 상품명', cols: ['nameKo', 'nameEn', 'nameJa', 'nameZh'] },
  { key: 'desc', label: '다국어 설명', cols: ['descKo', 'descEn', 'descJa', 'descZh'] },
  { key: 'detail', label: '상세 HTML(대용량 — 제외 시 가장 빠름)', cols: ['detailKo', 'detailEn', 'detailJa', 'detailZh'] },
  { key: 'etc', label: '상품정보고시·옵션', cols: ['noticeKo', 'variants'] },
];

const ALL_EXPORT_GROUP_KEYS = PRODUCT_EXPORT_FIELD_GROUPS.map((g) => g.key);

/**
 * 전체필드 export 쿼리스트링 — 검색 필터 + 선택 필드 그룹(fields CSV). selectedGroupKeys 가 전체(또는 미지정)면
 * fields 를 생략(=BE 가 26열 라운드트립 양식). 일부만 선택하면 해당 그룹의 컬럼 키만 CSV 로 보낸다(압축 export).
 */
export const buildProductExportFullQuery = (
  params: ProductsQueryParams,
  selectedGroupKeys?: readonly string[],
): string => {
  const qs = buildProductExportQuery(params);
  const keys = selectedGroupKeys ?? ALL_EXPORT_GROUP_KEYS;
  const isAll = ALL_EXPORT_GROUP_KEYS.every((k) => keys.includes(k));
  if (!isAll) {
    const cols = PRODUCT_EXPORT_FIELD_GROUPS.filter((g) => g.always || keys.includes(g.key))
      .flatMap((g) => g.cols);
    const params2 = new URLSearchParams(qs);
    params2.set('fields', cols.join(','));
    return params2.toString();
  }
  return qs;
};

export const PRODUCT_EXPORT_PATH = '/admin/v1/products/export';

/**
 * QA-90 (ID90) — 업로드용 예시(빈 양식) 엑셀 다운로드 경로. BE `GET /admin/v1/products/import-template`
 * 는 헤더 1행 + 컬럼별 입력 안내 1행만 있는 .xlsx 를 내려준다(9 컬럼 일괄 변경 양식).
 * 운영자가 이 양식을 받아 정보를 채워 업로드(import)하면 일괄 반영된다(빈 셀=미변경).
 */
export const PRODUCT_IMPORT_TEMPLATE_PATH = '/admin/v1/products/import-template';

/**
 * #17 — 상품 <b>전체 필드</b> 엑셀 경로(be-catalog ProductFullExcelService). 9컬럼 일괄변경과 별개.
 * - export-full: 현재 목록 필터 상품을 등록 양식(26 컬럼: 상품코드/바코드/유형/판매상태/진열/카테고리ID/
 *   브랜드명/정가/판매가 4통화/상품명·설명·상세HTML 4로케일/고시ko/옵션)으로 내보낸다(UUID 비노출).
 * - import-template-full: 헤더+컬럼별 안내 1행 빈 등록 양식.
 * - import-full: 상품코드 미존재=신규 등록(CREATE), 존재=전체 교체(UPDATE). 행별 부분 성공.
 *   식별 키는 <b>상품코드(productCode)</b>다(바코드 아님). 빈 상품코드/안내행은 무시.
 */
export const PRODUCT_EXPORT_FULL_PATH = '/admin/v1/products/export-full';
export const PRODUCT_IMPORT_TEMPLATE_FULL_PATH = '/admin/v1/products/import-template-full';

/** 전체 필드 export 파일명(ASCII, 날짜 부여). */
export const productExportFullFilename = (now: Date = new Date()): string => {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `products_full_${y}${m}${d}.xlsx`;
};

/** 전체 필드 업로드 양식 파일명(ASCII). */
export const productImportTemplateFullFilename = (): string =>
  'products-full-upload-template.xlsx';

/** 다운로드 파일명 — BE 도 products.xlsx(ASCII) 로 내려주지만 날짜를 붙여 BO 측 구분. */
export const productExportFilename = (now: Date = new Date()): string => {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `products_${y}${m}${d}.xlsx`;
};

/** QA-90 — 업로드 예시 양식 파일명(ASCII). BE 도 products-upload-template.xlsx 로 내려준다. */
export const productImportTemplateFilename = (): string => 'products-upload-template.xlsx';
